import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle
} from '@/components/ui/dialog';
import { Typography } from '@/components/ui/typography';
import type { FloorPlanTier } from './floorPlanOffer';
import {
  FLOOR_PLAN_TIER_CONTENT,
  type FloorPlanExampleFile
} from './floorPlanTierContent';
import { FloorPlanFeatureChips } from './FloorPlanFeatureChips';
import { FloorPlanPrice } from './FloorPlanPrice';

interface FloorPlanExampleDialogProps {
  /** The tier whose example is open; null closes the dialog. */
  tier: FloorPlanTier | null;
  onClose: () => void;
}

const SectionLabel = ({ children }: { children: string }) => (
  <Typography
    as="h3"
    variant="small-semibold"
    className="mb-2 block uppercase tracking-wide"
  >
    {children}
  </Typography>
);

const ExampleFiles = ({ files }: { files: FloorPlanExampleFile[] }) => (
  <ul className="grid grid-cols-3 gap-2">
    {files.map((file) => (
      <li key={file.label}>
        <figure className="flex flex-col gap-1">
          <img
            src={file.src}
            alt={file.label}
            loading="lazy"
            className="h-24 w-full rounded-md border border-border bg-background object-contain p-1"
          />
          <Typography as="figcaption" variant="small">
            {file.label}
          </Typography>
        </figure>
      </li>
    ))}
  </ul>
);

/**
 * A tier's example: the full plan, a close-up, and the files an order of it
 * delivers. Informational only — the dialog selects nothing, since Basic is
 * the only orderable tier and is always selected.
 */
export function FloorPlanExampleDialog({
  tier,
  onClose
}: FloorPlanExampleDialogProps) {
  const content = tier ? FLOOR_PLAN_TIER_CONTENT[tier.id] : null;

  return (
    <Dialog open={tier !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-[1180px] gap-0 p-0 sm:rounded-2xl">
        {tier && content && (
          <>
            <div className="flex items-center gap-3 border-b border-border px-5 py-3.5 pr-14">
              <DialogTitle className="text-lg font-semibold">
                {tier.name}
              </DialogTitle>
              <FloorPlanPrice tier={tier} />
              <DialogDescription className="sr-only">
                Example of a {tier.name}
                {tier.orderable ? '' : ', coming soon'}
              </DialogDescription>
            </div>

            <div className="grid gap-4 p-4 lg:grid-cols-[1.35fr_1fr]">
              <img
                src={content.exampleImage}
                alt={`${tier.name} example`}
                className="h-[min(56vh,560px)] w-full rounded-xl border border-border bg-background object-contain p-2"
              />

              <div className="flex flex-col gap-4">
                {content.closeUpImage && (
                  <section>
                    <SectionLabel>Close-up</SectionLabel>
                    <img
                      src={content.closeUpImage}
                      alt={`${tier.name} close-up`}
                      loading="lazy"
                      className="h-36 w-full rounded-md border border-border object-cover"
                    />
                  </section>
                )}

                {content.filesHeading && (
                  <section className="flex flex-col gap-3">
                    <SectionLabel>{content.filesHeading}</SectionLabel>
                    {content.fileGroups.map((group) => (
                      <div
                        key={group.title ?? 'files'}
                        className="flex flex-col gap-1.5"
                      >
                        {group.title && (
                          <Typography as="h4" variant="small-medium">
                            {group.title}
                          </Typography>
                        )}
                        <ExampleFiles files={group.files} />
                      </div>
                    ))}
                  </section>
                )}

                <FloorPlanFeatureChips content={content} layout="wrap" />
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
